import MapsService, { BoundingBox, GeoJsonType, GeometryType, LocationType, Position, Size } from "@churchofjesuschrist/maps-service";
import google, { isMVCArray, OverlayRegistry, OverlayType, toLatLng, toLatLngBounds, toSize } from "@churchofjesuschrist/maps-google";
import { getPathsBounds, openLoops, separatePolygonPaths } from "@churchofjesuschrist/maps-google-math";
import { deserializeProperties, getOverlayPaths, getOverlayType, getOverlayVisualProperties } from "@churchofjesuschrist/maps-google-overlays";
import { camelCase, isArray, isArrayOfArray, isArrayOfNotArray, isDefined, isNotEmpty, isObject, kebabCase, removeUndefined, toArray } from "@churchofjesuschrist/maps-utils";
class $GoogleMapsConverter {
  constructor() {
    this.register();
  }
  register() {
    MapsService.registerConverter(this);
  }
  convertFrom(value, type) {
    if (value && type) {
      const fn = `convertFrom${type}`;
      if (this[fn]) {
        return this[fn](value);
      }
    }
    return value;
  }
  convertTo(value, type) {
    if (value && type) {
      const fn = `convertTo${type}`;
      if (this[fn]) {
        return this[fn](value);
      }
    }
    return value;
  }
  convertFromPosition(position) {
    if (isArrayOfArray(position)) {
      return position.map(this.convertFromPosition, this).filter(isNotEmpty);
    } else {
      let results = toLatLng(position);
      if (results) {
        return results;
      }
    }
    return position;
  }
  convertToPosition(latLng) {
    if (isArray(latLng)) {
      return latLng.map(this.convertToPosition, this).filter(isNotEmpty);
    } else if (isMVCArray(latLng)) {
      return latLng.getArray().map(this.convertToPosition, this).filter(isNotEmpty);
    } else if (isObject(latLng)) {
      if (google.maps?.LatLng && latLng instanceof google.maps.LatLng) {
        latLng = latLng.toJSON();
      }
      let geojson = Position.toGeoJSON(latLng);
      if (geojson) {
        return geojson;
      }
    }
    return latLng;
  }
  convertFromBoundingBox(boundingBox) {
    if (isArrayOfArray(boundingBox)) {
      return boundingBox.map(this.convertFromBoundingBox, this).filter(isNotEmpty);
    } else {
      let bounds = toLatLngBounds(boundingBox);
      if (bounds) {
        return bounds;
      }
    }
    return boundingBox;
  }
  convertToBoundingBox(latLngBounds) {
    if (isArray(latLngBounds)) {
      return latLngBounds.map(this.convertToBoundingBox, this).filter(isNotEmpty);
    } else if (isMVCArray(latLngBounds)) {
      return latLngBounds.getArray().map(this.convertToBoundingBox, this).filter(isNotEmpty);
    } else if (isObject(latLngBounds)) {
      if (google.maps?.LatLngBounds && latLngBounds instanceof google.maps.LatLngBounds) {
        latLngBounds = latLngBounds.toJSON();
      }
      let geojson = BoundingBox.toGeoJSON(latLngBounds);
      if (geojson) {
        return geojson;
      }
    }
  }
  convertFromGeoJson(geojson) {
    if (isArray(geojson)) {
      return geojson.map(this.convertFromGeoJson, this).filter(isNotEmpty);
    } else if (isObject(geojson) && geojson.type) {
      switch (geojson.type) {
        case GeoJsonType.FEATURE:
          return this.convertFromFeature(geojson);
        case GeoJsonType.FEATURE_COLLECTION:
          return this.convertFromFeatureCollection(geojson);
        default:
          return this.convertFromGeometry(geojson);
      }
    }
    return geojson;
  }
  convertToGeoJson(value) {
    if (isArray(value) || isMVCArray(value)) {
      let entries = value.map(this.convertToGeoJson, this).filter(isNotEmpty);
      let type = entries.length ? entries[0].type : null;
      if (type) {
        if (type === GeoJsonType.FEATURE) {
          return {
            type: GeoJsonType.FEATURE_COLLECTION,
            features: entries
          };
        } else {
          return {
            type: GeoJsonType.GEOMETRY_COLLECTION,
            geometries: entries
          };
        }
      }
    } else if (isObject(value)) {
      if (value.toGeoJSON) {
        return value.toGeoJSON();
      } else if (value.toGeometry) {
        return value.toGeometry();
      }
      let overlayType = getOverlayType(value);
      if (overlayType) {
        return this.convertToGeometry(value);
      }
      return this.convertToFeature(value);
    }
    return value;
  }
  convertFromGeometry(geometry) {
    if (isArray(geometry)) {
      return geometry.map(this.convertFromGeometry, this).filter(isNotEmpty);
    } else if (isObject(geometry) && geometry.type) {
      switch (geometry.type) {
        case GeometryType.POINT:
          return this.convertFromPoint(geometry);
        case GeometryType.MULTI_POINT:
          return this.convertFromMultiPoint(geometry);
        case GeometryType.LINE_STRING:
          return this.convertFromLineString(geometry);
        case GeometryType.MULTI_LINE_STRING:
          return this.convertFromMultiLineString(geometry);
        case GeometryType.POLYGON:
          return this.convertFromPolygon(geometry);
        case GeometryType.MULTI_POLYGON:
          return this.convertFromMultiPolygon(geometry);
        case GeometryType.GEOMETRY_COLLECTION:
          return this.convertFromGeometryCollection(geometry);
      }
    }
    return geometry;
  }
  convertToGeometry(overlay) {
    if (isArray(overlay) || isMVCArray(overlay)) {
      return this.convertToGeometryCollection(overlay);
    } else if (isObject(overlay)) {
      if (overlay.toGeometry) {
        return overlay.toGeometry();
      } else if (overlay.toGeoJSON) {
        return overlay.toGeoJSON();
      }
      let overlayType = getOverlayType(overlay);
      if (overlayType) {
        switch (overlayType) {
          case OverlayType.CIRCLE:
          case OverlayType.RECTANGLE:
            return this.convertToPolygon(overlay);
          case OverlayType.POLYGON:
            return this.convertToMultiPolygon(overlay);
          case OverlayType.POLYLINE:
            return this.convertToLineString(overlay);
          case OverlayType.MARKER:
            return this.convertToPoint(overlay);
          case OverlayType.CUSTOM:
            if (overlay.getPosition) {
              return this.convertToPoint(overlay);
            }
            break;
        }
      }
    }
    return overlay;
  }
  convertFromPoint(point) {
    if (isArray(point)) {
      return point.map(this.convertFromPoint, this).filter(isNotEmpty);
    } else if (isObject(point) && point.type === GeometryType.POINT) {
      return new google.maps.Marker({
        position: this.convertFromPosition(point.coordinates)
      });
    }
    return point;
  }
  convertToPoint(marker) {
    if (isArray(marker)) {
      return marker.map(this.convertToPoint, this).filter(isNotEmpty);
    } else if (isMVCArray(marker)) {
      return marker.getArray().map(this.convertToPoint, this).filter(isNotEmpty);
    } else if (isObject(marker)) {
      let position = marker.getPosition();
      return {
        type: GeometryType.POINT,
        coordinates: this.convertToPosition(position)
      };
    }
    return marker;
  }
  convertFromLineString(lineString) {
    if (isArray(lineString)) {
      return lineString.map(this.convertFromLineString, this).filter(isNotEmpty);
    } else if (isObject(lineString) && lineString.type === GeometryType.LINE_STRING) {
      let path;
      if (lineString.encoded) {
        path = google.maps.geometry.encoding.decodePath(lineString.encoded);
      } else {
        path = this.convertFromPosition(lineString.coordinates);
      }
      return new google.maps.Polyline({
        path
      });
    }
    return lineString;
  }
  convertToLineString(polyline) {
    if (isArray(polyline)) {
      return polyline.map(this.convertToLineString, this).filter(isNotEmpty);
    } else if (isMVCArray(polyline)) {
      return polyline.getArray().map(this.convertToLineString, this).filter(isNotEmpty);
    } else if (isObject(polyline)) {
      let path = polyline.getPath();
      return {
        type: GeometryType.LINE_STRING,
        coordinates: this.convertToPosition(path)
      };
    }
    return polyline;
  }
  convertFromPolygon(polygon) {
    if (isArray(polygon)) {
      return polygon.map(this.convertFromPolygon, this).filter(isNotEmpty);
    } else if (isObject(polygon) && polygon.type === GeometryType.POLYGON) {
      let paths;
      if (polygon.encoded) {
        paths = polygon.encoded.map(path => google.maps.geometry.encoding.decodePath(path));
      } else {
        paths = this.convertFromPosition(polygon.coordinates);
      }
      return new google.maps.Polygon({
        paths
      });
    }
    return polygon;
  }
  convertToPolygon(overlay) {
    if (isArray(overlay)) {
      return overlay.map(this.convertToPolygon, this).filter(isNotEmpty);
    } else if (isMVCArray(overlay)) {
      return overlay.getArray().map(this.convertToPolygon, this).filter(isNotEmpty);
    } else if (isObject(overlay)) {
      let paths = getOverlayPaths(overlay, true);
      if (paths) {
        return {
          type: GeometryType.POLYGON,
          coordinates: this.convertToPosition(paths)
        };
      }
    }
    return overlay;
  }
  convertFromMultiPoint(value) {
    //TODO
    return value;
  }
  convertToMultiPoint(value) {
    //TODO
    return value;
  }
  convertFromMultiLineString(multiLineString) {
    //TODO
    return multiLineString;
  }
  convertToMultiLineString(polyline) {
    //TODO
    return polyline;
  }
  convertFromMultiPolygon(multiPolygon) {
    if (isArray(multiPolygon)) {
      return multiPolygon.map(this.convertFromMultiPolygon, this).filter(isNotEmpty);
    } else if (isObject(multiPolygon) && multiPolygon.type === GeometryType.MULTI_POLYGON) {
      let paths;
      if (multiPolygon.encoded) {
        paths = multiPolygon.encoded.map(polygon => polygon.map(path => google.maps.geometry.encoding.decodePath(path)));
      } else {
        paths = this.convertFromPosition(multiPolygon.coordinates);
      }
      if (paths?.length) {
        paths = paths.flat();
        paths = openLoops(paths);
        return new google.maps.Polygon({
          paths
        });
      } else {
        return null;
      }
    }
    return multiPolygon;
  }
  convertToMultiPolygon(polygon) {
    if (isArray(polygon)) {
      return polygon.map(this.convertToMultiPolygon, this).filter(isNotEmpty);
    } else if (isMVCArray(polygon)) {
      return polygon.getArray().map(this.convertToMultiPolygon, this).filter(isNotEmpty);
    } else if (isObject(polygon)) {
      let polygonPaths = separatePolygonPaths(polygon, true);
      //TODO: correct direction if necessary
      return {
        type: GeometryType.MULTI_POLYGON,
        coordinates: this.convertToPosition(polygonPaths)
      };
    }
    return polygon;
  }
  convertFromGeometryCollection(geometryCollection) {
    if (isObject(geometryCollection) && geometryCollection.type === GeometryType.GEOMETRY_COLLECTION) {
      return geometryCollection.geometries.map(this.convertFromGeometry, this).filter(isNotEmpty);
    }
    return geometryCollection;
  }
  convertToGeometryCollection(overlays) {
    if (!isArray(overlays) && isObject(overlays) && overlays.type !== GeometryType.GEOMETRY_COLLECTION) {
      overlays = [overlays];
    }
    if (isMVCArray(overlays)) {
      overlays = overlays.getArray();
    }
    if (isArray(overlays)) {
      return {
        type: GeometryType.GEOMETRY_COLLECTION,
        geometries: overlays.map(this.convertToGeometry, this).filter(isNotEmpty)
      };
    }
    return overlays;
  }
  convertFromFeature(feature) {
    if (isArray(feature)) {
      return feature.map(this.convertFromFeature, this).filter(isNotEmpty);
    } else if (isObject(feature)) {
      let overlays = null,
        properties = feature.properties || {};
      let {
        type,
        $class,
        ...rest
      } = properties;
      properties = this.convertFromProperties(rest);
      if (type || $class) {
        if ($class) {
          let Clazz = OverlayRegistry.getClass($class);
          if (Clazz?.create) {
            overlays = Clazz.create(feature);
          } else if (feature.geometry?.type === GeometryType.POINT) {
            overlays = new Clazz({
              position: this.convertFromPosition(feature.geometry.coordinates)
            });
          }
        }
        if (!overlays && type) {
          switch (type) {
            case OverlayType.CIRCLE:
              {
                let {
                  radius,
                  center,
                  ...rest
                } = properties;
                if (isDefined(radius) && isDefined(center)) {
                  properties = rest;
                  center = this.convertFromPosition(center);
                  overlays = new google.maps.Circle({
                    center,
                    radius
                  });
                }
                break;
              }
            case OverlayType.RECTANGLE:
              overlays = this.convertFromGeometry(feature.geometry);
              overlays = new google.maps.Rectangle({
                bounds: getPathsBounds(overlays.getPaths())
              });
              break;
          }
        }
      }
      if (!overlays) {
        overlays = this.convertFromGeometry(feature.geometry);
      }
      if (overlays && properties) {
        toArray(overlays).forEach(overlay => {
          let options = deserializeProperties(properties);
          overlay.setOptions(options);
        });
      }
      if (!$class && type && properties.id && !isArray(overlays) && Object.keys(LocationType).includes(type)) {
        let location = this.convertFromLocation(properties);
        if (getOverlayType(overlays) === OverlayType.POLYGON) {
          location.boundary = overlays;
        }
        overlays = location;
      }
      return overlays;
    }
    return feature;
  }
  convertToFeature(value, includeProperties = null) {
    if (isArray(value)) {
      return value.map(it => this.convertToFeature(it, includeProperties)).filter(isNotEmpty);
    } else if (isMVCArray(value)) {
      return value.getArray().map(it => this.convertToFeature(it, includeProperties)).filter(isNotEmpty);
    } else if (isObject(value)) {
      let geometry = value,
        properties = {};
      let type = getOverlayType(value);
      if (type) {
        properties = getOverlayVisualProperties(value, includeProperties);
        properties.type = type;
        let name = OverlayRegistry.getName(value) || value.constructor?.name;
        if (name) {
          properties.$class = name;
        }
        switch (properties.type) {
          case OverlayType.CIRCLE:
            properties.radius = value.getRadius();
            properties.center = this.convertToPosition(value.getCenter());
            break;
        }
      } else if (value.id || value.type || value.boundary) {
        geometry = value.boundary;
        properties.id = value.id;
        properties.type = value.type;
        properties.name = value.name;
        properties.coordinates = this.convertToPosition(value.coordinates);
      }
      properties = this.convertToProperties(properties);
      if (value.toFeature) {
        return value.toFeature(properties);
      }
      return {
        type: GeoJsonType.FEATURE,
        geometry: this.convertToGeometry(geometry),
        properties
      };
    }
    return value;
  }
  convertFromFeatureCollection(featureCollection) {
    if (isObject(featureCollection) && featureCollection.type === GeoJsonType.FEATURE_COLLECTION) {
      return featureCollection.features.map(this.convertFromFeature, this).flat().filter(isNotEmpty);
    }
    return featureCollection;
  }
  convertToFeatureCollection(overlays) {
    if (!isArray(overlays) && isObject(overlays) && overlays.type !== GeoJsonType.FEATURE_COLLECTION) {
      overlays = [overlays];
    }
    if (isMVCArray(overlays)) {
      overlays = overlays.getArray();
    }
    if (isArray(overlays)) {
      return {
        type: GeoJsonType.FEATURE_COLLECTION,
        features: overlays.map(it => this.convertToFeature(it)).filter(isNotEmpty)
      };
    }
    return overlays;
  }
  convertFromProperties(properties) {
    if (isArray(properties)) {
      return properties.map(this.convertFromProperties, this).filter(isNotEmpty);
    } else if (isObject(properties)) {
      // https://github.com/mapbox/simplestyle-spec/blob/master/1.1.0/README.md
      //TODO: simplestyle icon
      const altKeys = {
        fill: "fillColor",
        stroke: "strokeColor",
        strokeWidth: "stokeWeight"
      };
      return Object.entries(properties).reduce((props, [key, value]) => {
        let newKey = camelCase(key);
        newKey = altKeys[newKey] || newKey;
        props[newKey] = value;
        return props;
      }, {});
    }
    return properties;
  }
  convertToProperties(properties) {
    if (isArray(properties)) {
      return properties.map(this.convertToProperties, this).filter(isNotEmpty);
    } else if (isMVCArray(properties)) {
      return properties.getArray().map(this.convertToProperties, this).filter(isNotEmpty);
    } else if (isObject(properties)) {
      // https://github.com/mapbox/simplestyle-spec/blob/master/1.1.0/README.md
      //TODO: simplestyle icon
      const altKeys = {
        class: "$class",
        "fill-color": "fill",
        "stroke-color": "stroke",
        "stoke-weight": "stroke-width"
      };
      return Object.entries(properties).reduce((props, [key, value]) => {
        let newKey = kebabCase(key);
        newKey = altKeys[newKey] || newKey;
        props[newKey] = value;
        return props;
      }, {});
    }
    return properties;
  }
  convertFromPositionBox(positionBox) {
    if (isArray(positionBox)) {
      return positionBox.map(this.convertFromPositionBox, this).filter(isNotEmpty);
    } else if (isObject(positionBox)) {
      return {
        ...positionBox,
        coordinates: this.convertFromPosition(positionBox.coordinates),
        extent: this.convertFromBoundingBox(positionBox.extent)
      };
    }
    return positionBox;
  }
  convertToPositionBox(positionBox) {
    if (isArray(positionBox)) {
      return positionBox.map(this.convertToPositionBox, this).filter(isNotEmpty);
    } else if (isMVCArray(positionBox)) {
      return positionBox.getArray().map(this.convertToPositionBox, this).filter(isNotEmpty);
    } else if (isObject(positionBox)) {
      return {
        ...positionBox,
        coordinates: this.convertToPosition(positionBox.coordinates),
        extent: this.convertToBoundingBox(positionBox.extent)
      };
    }
    return positionBox;
  }
  convertFromSize(size) {
    if (isArrayOfArray(size) || isArrayOfNotArray(size)) {
      return size.map(this.convertFromSize, this).filter(isNotEmpty);
    } else {
      let results = toSize(size);
      if (results) {
        return results;
      }
    }
    return size;
  }
  convertToSize(size) {
    if (isArray(size)) {
      return size.map(this.convertToSize, this).filter(isNotEmpty);
    } else if (isMVCArray(size)) {
      return size.getArray().map(this.convertToSize, this).filter(isNotEmpty);
    } else if (isObject(size)) {
      let json = Size.toJSON(size);
      if (json) {
        return json;
      }
    }
    return size;
  }
  convertFromIntersection(intersection) {
    if (isArray(intersection)) {
      return intersection.map(this.convertFromIntersection, this).filter(isNotEmpty);
    } else if (isObject(intersection)) {
      return {
        ...intersection,
        coordinates: this.convertFromPosition(intersection.coordinates),
        connections: this.convertFromIntersectionConnection(intersection.connections)
      };
    }
    return intersection;
  }
  convertToIntersection(intersection) {
    if (isArray(intersection)) {
      return intersection.map(this.convertToIntersection, this).filter(isNotEmpty);
    } else if (isMVCArray(intersection)) {
      return intersection.getArray().map(this.convertToIntersection, this).filter(isNotEmpty);
    } else if (isObject(intersection)) {
      return {
        ...intersection,
        coordinates: this.convertToPosition(intersection.coordinates),
        connections: this.convertToIntersectionConnection(intersection.connections)
      };
    }
    return intersection;
  }
  convertFromIntersectionConnection(connection) {
    if (isArray(connection)) {
      return connection.map(this.convertFromIntersectionConnection, this).filter(isNotEmpty);
    } else if (isObject(connection)) {
      return {
        ...connection,
        segment: this.convertFromLineString(connection.segment)
      };
    }
    return connection;
  }
  convertToIntersectionConnection(connection) {
    if (isArray(connection)) {
      return connection.map(this.convertToIntersectionConnection, this).filter(isNotEmpty);
    } else if (isMVCArray(connection)) {
      return connection.getArray().map(this.convertToIntersectionConnection, this).filter(isNotEmpty);
    } else if (isObject(connection)) {
      return {
        ...connection,
        segment: this.convertToLineString(connection.segment)
      };
    }
    return connection;
  }
  convertFromCluster(cluster) {
    if (isArray(cluster)) {
      return cluster.map(this.convertFromCluster, this).filter(isNotEmpty);
    } else if (isObject(cluster)) {
      return {
        ...cluster,
        coordinates: this.convertFromPosition(cluster.coordinates),
        extent: this.convertFromBoundingBox(cluster.extent)
      };
    }
    return cluster;
  }
  convertToCluster(cluster) {
    if (isArray(cluster)) {
      return cluster.map(this.convertToCluster, this).filter(isNotEmpty);
    } else if (isMVCArray(cluster)) {
      return cluster.getArray().map(this.convertToCluster, this).filter(isNotEmpty);
    } else if (isObject(cluster)) {
      return {
        ...cluster,
        coordinates: this.convertToPosition(cluster.coordinates),
        extent: this.convertToBoundingBox(cluster.extent)
      };
    }
    return cluster;
  }
  convertFromLocation(location) {
    if (isArray(location)) {
      return location.map(this.convertFromLocation, this).filter(isNotEmpty);
    } else if (isObject(location)) {
      return removeUndefined({
        ...location,
        coordinates: this.convertFromPosition(location.coordinates),
        extent: this.convertFromBoundingBox(location.extent),
        centroids: this.convertFromPosition(location.centroids),
        labels: this.convertFromPositionBox(location.labels),
        boundary: this.convertFromMultiPolygon(location.boundary),
        facility: this.convertFromLocation(location.facility),
        parent: this.convertFromLocation(location.parent),
        children: this.convertFromLocation(location.children),
        associated: this.convertFromLocation(location.associated)
      });
    }
    return location;
  }
  convertToLocation(location) {
    if (isArray(location)) {
      return location.map(this.convertToLocation, this).filter(isNotEmpty);
    } else if (isMVCArray(location)) {
      return location.getArray().map(this.convertToLocation, this).filter(isNotEmpty);
    } else if (isObject(location)) {
      return removeUndefined({
        ...location,
        coordinates: this.convertToPosition(location.coordinates),
        extent: this.convertToBoundingBox(location.extent),
        centroids: this.convertToPosition(location.centroids),
        labels: this.convertToPositionBox(location.labels),
        boundary: this.convertToMultiPolygon(location.boundary),
        facility: this.convertToLocation(location.facility),
        parent: this.convertToLocation(location.parent),
        children: this.convertToLocation(location.children),
        associated: this.convertToLocation(location.associated)
      });
    }
    return location;
  }
  convertFromRoute(route) {
    if (isArray(route)) {
      return route.map(this.convertFromRoute, this).filter(isNotEmpty);
    } else if (isObject(route)) {
      return removeUndefined({
        ...route,
        legs: this.convertFromRouteLeg(route.legs),
        extent: this.convertFromBoundingBox(route.extent),
        path: this.convertFromLineString(route.path)
      });
    }
    return route;
  }
  convertToRoute(route) {
    if (isArray(route)) {
      return route.map(this.convertToRoute, this).filter(isNotEmpty);
    } else if (isMVCArray(route)) {
      return route.getArray().map(this.convertToRoute, this).filter(isNotEmpty);
    } else if (isObject(route)) {
      return removeUndefined({
        ...route,
        legs: this.convertToRouteLeg(route.legs),
        extent: this.convertToBoundingBox(route.extent),
        path: this.convertToLineString(route.path)
      });
    }
    return route;
  }
  convertFromRouteLeg(leg) {
    if (isArray(leg)) {
      return leg.map(this.convertFromRouteLeg, this).filter(isNotEmpty);
    } else if (isObject(leg)) {
      return removeUndefined({
        ...leg,
        start: this.convertFromLocation(leg.start),
        end: this.convertFromLocation(leg.end),
        steps: this.convertFromRouteStep(leg.steps)
      });
    }
    return leg;
  }
  convertToRouteLeg(leg) {
    if (isArray(leg)) {
      return leg.map(this.convertToRouteLeg, this).filter(isNotEmpty);
    } else if (isMVCArray(leg)) {
      return leg.getArray().map(this.convertToRouteLeg, this).filter(isNotEmpty);
    } else if (isObject(leg)) {
      return removeUndefined({
        ...leg,
        start: this.convertToLocation(leg.start),
        end: this.convertToLocation(leg.end),
        steps: this.convertToRouteStep(leg.steps)
      });
    }
    return leg;
  }
  convertFromRouteStep(step) {
    if (isArray(step)) {
      return step.map(this.convertFromRouteStep, this).filter(isNotEmpty);
    } else if (isObject(step)) {
      return removeUndefined({
        ...step,
        start: this.convertFromPosition(step.start),
        end: this.convertFromPosition(step.end),
        steps: this.convertFromRouteStep(step.steps)
      });
    }
    return step;
  }
  convertToRouteStep(step) {
    if (isArray(step)) {
      return step.map(this.convertToRouteStep, this).filter(isNotEmpty);
    } else if (isMVCArray(step)) {
      return step.getArray().map(this.convertToRouteStep, this).filter(isNotEmpty);
    } else if (isObject(step)) {
      return removeUndefined({
        ...step,
        start: this.convertToPosition(step.start),
        end: this.convertToPosition(step.end),
        steps: this.convertToRouteStep(step.steps)
      });
    }
    return step;
  }
  convertToMapMetadata(map) {
    if (map) {
      return removeUndefined({
        "map-center": this.convertToPosition(map.getCenter()),
        "map-zoom": map.getZoom(),
        "map-extent": this.convertToBoundingBox(map.getBounds()),
        "map-type": map.getMapTypeId()
      });
    }
    return null;
  }
  convertFromMapMetadata(metadata) {
    if (metadata) {
      return removeUndefined({
        center: this.convertFromPosition(metadata["map-center"]),
        zoom: metadata["map-zoom"],
        bounds: this.convertFromBoundingBox(metadata["map-extent"]),
        mapTypeId: metadata["map-type"]
      });
    }
    return null;
  }
}
const GoogleMapsConverter = new $GoogleMapsConverter();
Object.freeze(GoogleMapsConverter);
export default GoogleMapsConverter;